import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AssetVersionedView } from '@trackly/shared';
import { AssetView } from '../../../services/AssetService/types/assetTypes';
import { createDefaultAsset } from '../../../services/AssetService/createDefaultAsset';
import { assetQueriesApi } from '../../../services/AssetService/assetQueries';
import { assetMutationsApi } from '../../../services/AssetService/assetMutations';

interface AssetState {
  // Carries the version (ETag) the asset was read or last saved at — the shared getAssetById and
  // upsertAsset endpoints attach it — so the save button can send it back and a save made
  // elsewhere in between is detected instead of silently overwritten.
  currentAsset: AssetVersionedView | null;
  // The asset exactly as last read or saved: the baseline the server merges the draft against when
  // a save finds a newer version. Edits never touch it.
  baseAsset: AssetVersionedView | null;
  selectedAssetId: string | null;
  isEdited: boolean;
  editedFields: string[];
  filters: {
    status: string | null;
    center: string | null;
    category: string | null;
    searchTerm: string;
  };
}

const initialState: AssetState = {
  currentAsset: null,
  baseAsset: null,
  selectedAssetId: null,
  isEdited: false,
  editedFields: [],
  filters: {
    status: null,
    center: null,
    category: null,
    searchTerm: '',
  },
};

const addEditedField = (state: AssetState, fieldName: string) => {
  if (!state.editedFields.includes(fieldName)) {
    state.editedFields.push(fieldName);
  }
  state.isEdited = true;
};

const assetSlice = createSlice({
  name: 'asset',
  initialState,
  reducers: {
    updateAsset: (state, action: PayloadAction<Partial<AssetView>>) => {
      if (state.currentAsset) {
        state.currentAsset = { ...state.currentAsset, ...action.payload };
        Object.keys(action.payload).forEach(field => {
          if (!state.editedFields.includes(field)) {
            state.editedFields.push(field);
          }
        });
        state.isEdited = true;
      }
    },

    initializeAsset: (state, action: PayloadAction<string | undefined>) => {
      state.currentAsset = createDefaultAsset(action.payload);
      state.baseAsset = null;
      state.isEdited = false;
      state.editedFields = [];
    },

    clearAsset: (state) => {
      state.currentAsset = null;
      state.baseAsset = null;
      state.selectedAssetId = null;
      state.isEdited = false;
      state.editedFields = [];
    },

    setSelectedAssetId: (state, action: PayloadAction<string | null>) => {
      state.selectedAssetId = action.payload;
    },

    // ── Identification ─────────────────────────────────────────────────
    updateAssetIdentification: (state, action: PayloadAction<{
      name?: string;
      assetTag?: string;
      description?: string;
      serialNumber?: string;
      manufacturer?: string;
      model?: string;
      category?: string;
    }>) => {
      if (state.currentAsset) {
        const updates = action.payload;
        if (updates.name !== undefined) state.currentAsset.name = updates.name;
        if (updates.assetTag !== undefined) state.currentAsset.assetTag = updates.assetTag;
        if (updates.description !== undefined) state.currentAsset.description = updates.description;
        if (updates.serialNumber !== undefined) state.currentAsset.serialNumber = updates.serialNumber;
        if (updates.manufacturer !== undefined) state.currentAsset.manufacturer = updates.manufacturer;
        if (updates.model !== undefined) state.currentAsset.model = updates.model;
        if (updates.category !== undefined) state.currentAsset.category = updates.category;
        addEditedField(state, 'identification');
      }
    },

    // ── Location ───────────────────────────────────────────────────────
    updateAssetLocation: (state, action: PayloadAction<{
      center?: string;
      building?: string;
      floor?: string;
      room?: string;
    }>) => {
      if (state.currentAsset) {
        const updates = action.payload;
        if (updates.center !== undefined) state.currentAsset.center = updates.center;
        if (updates.building !== undefined) state.currentAsset.building = updates.building;
        if (updates.floor !== undefined) state.currentAsset.floor = updates.floor;
        if (updates.room !== undefined) state.currentAsset.room = updates.room;
        addEditedField(state, 'location');
      }
    },

    // ── Status ─────────────────────────────────────────────────────────
    updateAssetStatus: (state, action: PayloadAction<{
      status?: string;
      installDate?: string;
      warrantyExpiration?: string;
      expectedLifespan?: string;
    }>) => {
      if (state.currentAsset) {
        const updates = action.payload;
        if (updates.status !== undefined) state.currentAsset.status = updates.status;
        if (updates.installDate !== undefined) state.currentAsset.installDate = updates.installDate;
        if (updates.warrantyExpiration !== undefined) state.currentAsset.warrantyExpiration = updates.warrantyExpiration;
        if (updates.expectedLifespan !== undefined) state.currentAsset.expectedLifespan = updates.expectedLifespan;
        addEditedField(state, 'status');
      }
    },

    // ── Notes ──────────────────────────────────────────────────────────
    updateAssetNotes: (state, action: PayloadAction<{
      notes?: string;
    }>) => {
      if (state.currentAsset) {
        const updates = action.payload;
        if (updates.notes !== undefined) state.currentAsset.notes = updates.notes;
        addEditedField(state, 'notes');
      }
    },

    // ── Filters ────────────────────────────────────────────────────────
    setStatusFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.status = action.payload;
    },
    setCenterFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.center = action.payload;
    },
    setCategoryFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.category = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.filters.searchTerm = action.payload;
    },
    clearFilters: (state) => {
      state.filters = { status: null, center: null, category: null, searchTerm: '' };
    },

    // ── General ────────────────────────────────────────────────────────
    resetAsset: (state) => {
      state.currentAsset = null;
      state.baseAsset = null;
      state.selectedAssetId = null;
      state.isEdited = false;
      state.editedFields = [];
    },
    markAsSaved: (state) => { state.isEdited = false; state.editedFields = []; },
    discardChanges: (state) => { state.isEdited = false; state.editedFields = []; },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        assetQueriesApi.endpoints.getAssetById.matchFulfilled,
        (state, action) => {
          state.currentAsset = action.payload;
          state.baseAsset = action.payload;
          state.selectedAssetId = action.payload.id;
          state.isEdited = false;
          state.editedFields = [];
        }
      )
      .addMatcher(
        assetMutationsApi.endpoints.upsertAsset.matchFulfilled,
        (state, action) => {
          // `merged` describes this response, not the asset, so it must not enter the draft that
          // is sent back on the next save.
          const { merged: _merged, ...saved } = action.payload;
          state.currentAsset = saved;
          state.baseAsset = saved;
          if (saved.id) {
            state.selectedAssetId = saved.id;
          }
          state.isEdited = false;
          state.editedFields = [];
        }
      );
  },
});

export const {
  updateAsset,
  initializeAsset,
  clearAsset,
  setSelectedAssetId,
  updateAssetIdentification,
  updateAssetLocation,
  updateAssetStatus,
  updateAssetNotes,
  setStatusFilter,
  setCenterFilter,
  setCategoryFilter,
  setSearchTerm,
  clearFilters,
  resetAsset,
  markAsSaved,
  discardChanges,
} = assetSlice.actions;

export const assetReducer = assetSlice.reducer;
export default assetReducer;
